import React, { useState, useEffect } from 'react';
import SelectFieldCheckbox from './SelectFieldCheckbox';
import PlaceHolderField from '../PlaceHolderField';
import ErrorMessage from '../../../ErrorMessage';
import TimeSheetEntryService from '../../../../Services/timeSheetEntryService';

/**
 * Billed Office selector for outsourced employees.
 * Loads the offices the user can bill against from their FTE entries for the given start date.
 * Emits billedOfficeId + billedOfficeName together through changeOfEditedMultipItem
 * (same pattern as OutsourcedUserSelect), so the parent receives both fields in one update.
 *
 * Extra props (forwarded by renderBilledOfficeSelect):
 *  - startDate: entry start date (e.g. '2026.04.29' or '2026-04-29')
 *  - onNoFteEntries: optional callback notified whether FTE entries are missing for the date
 *  - userOfficeName: label used when the current value is outside the loaded options
 */
function BilledOfficeSelect({
  value,
  className,
  fieldName,
  onChange,
  filterCleared,
  changeOfEditedMultipItem,
  startDate,
  onNoFteEntries,
  userOfficeName,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noFteEntries, setNoFteEntries] = useState(false);

  const emitChange = (officeId, officeName) => {
    if (changeOfEditedMultipItem) {
      changeOfEditedMultipItem([
        { fieldName: fieldName, newValue: officeId },
        { fieldName: 'billedOfficeName', newValue: officeName },
      ]);
    } else {
      onChange(fieldName, officeId);
    }
  };

  useEffect(() => {
    const entryDate = (startDate || '').split('.').join('-');
    setLoading(true);
    TimeSheetEntryService.GetOutsourcedOfficeOptions(entryDate)
      .then((result) => {
        if (result.noFteEntries) {
          setNoFteEntries(true);
          setLoading(false);
          if (onNoFteEntries) onNoFteEntries(true);
          ErrorMessage.SetWarning(
            'FTE tracking is not set up for the current month. Please contact your HR representative or line manager to set up FTE tracking before submitting Activity Tracker entries.'
          );
          return;
        }

        const officeOptions = (result.offices || []).map((o) => ({
          value: o.officeId,
          label: o.officeName,
        }));
        setOptions(officeOptions);
        setNoFteEntries(false);
        setLoading(false);
        if (onNoFteEntries) onNoFteEntries(false);

        // Clear selection if it's no longer available
        if (value && !officeOptions.find((o) => o.value === value)) {
          emitChange(null, '');
        }

        // Set default if nothing selected
        if (result.defaultOfficeId && !value) {
          const defaultOption = officeOptions.find((o) => o.value === result.defaultOfficeId);
          if (defaultOption) {
            emitChange(defaultOption.value, defaultOption.label);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        ErrorMessage.SetErrorMessage('TSEE_OFFICE_OPTIONS');
      });
  }, [startDate]);

  // If value is set but not in options (e.g. user's office when no FTE entries), add it
  const effectiveOptions =
    value && !options.find((o) => String(o.value) === String(value))
      ? [...options, { value, label: userOfficeName || `Office ${value}` }]
      : options;

  if (loading || (noFteEntries && !value)) {
    return (
      <PlaceHolderField
        className={className}
        fieldName={fieldName}
      />
    );
  }

  // When no FTE entries exist with a value set (e.g. user's office auto-filled), show as read-only text
  if (noFteEntries) {
    const displayLabel = effectiveOptions.find((o) => String(o.value) === String(value))?.label || `Office ${value}`;
    return (
      <input
        type='text'
        className={className || 'w-100 form-control form-control-sm'}
        value={displayLabel}
        id={`htmlidfor${fieldName}`}
        readOnly
      />
    );
  }

  const selectValues = [{ value: '', label: 'Select office' }, ...effectiveOptions];

  return (
    <SelectFieldCheckbox
      value={value || ''}
      className={className || 'w-100 form-control form-control-sm'}
      fieldName={fieldName}
      onChange={(fName, newValue) => {
        const selected = options.find((o) => String(o.value) === String(newValue));
        emitChange(selected ? selected.value : null, selected ? selected.label : '');
      }}
      filterCleared={filterCleared}
      selectValues={selectValues}
    />
  );
}

export default BilledOfficeSelect;
